import { type TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Skeleton } from '@sorare/core/src/atoms/animations/Skeleton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { formatSearchString } from '@sorare/core/src/lib/string/formatSearchString';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CollectionPreviewHeader } from '../CollectionPreviewHeader';
import { CollectionSlot } from '../CollectionSlot';
import type { CollectionSlotsPreview_cardCollection } from './__generated__/index.graphql';

const Root = styled(Vertical)`
  isolation: isolate;
`;
const SlotsGrid = styled.div`
  display: grid;
  gap: var(--unit);
  grid-template-columns: repeat(6, minmax(0, 1fr));
  &.small {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  &.large {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  @media ${laptopAndAbove} {
    &,
    &.small {
      grid-template-columns: repeat(auto-fill, minmax(60px, 1fr));
    }
    &.large {
      grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    }
  }
`;

const HeaderWrapper = styled.div`
  flex: 1;
`;

type Props = {
  cardCollection: Nullable<CollectionSlotsPreview_cardCollection>;
  small?: boolean;
  playerNameFilter?: string;
};

export const Fallback = ({ small = false }: { small?: boolean }) => {
  return (
    <Root gap={2}>
      <HeaderWrapper />
      <Skeleton
        style={{
          width: '100%',
          height: small ? 300 : 400,
        }}
      />
    </Root>
  );
};

export const CollectionSlotsPreview = ({
  cardCollection,
  small = false,
  playerNameFilter,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const nbSlots = small ? 16 : undefined;

  if (!cardCollection?.slug) {
    return null;
  }
  return (
    <Root gap={2}>
      <HeaderWrapper
        as={Link}
        to={generateSportContextPath(
          ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG,
          {
            params: {
              collectionSlug: cardCollection?.slug,
            },
          }
        )}
      >
        <CollectionPreviewHeader cardCollection={cardCollection} />
      </HeaderWrapper>
      <SlotsGrid
        className={classNames({
          small: small && !playerNameFilter,
          large: playerNameFilter,
        })}
      >
        {cardCollection?.userCardCollection.slots
          .slice(0, nbSlots ?? undefined)
          .filter(slot => {
            if (!playerNameFilter) {
              return true;
            }
            return formatSearchString(
              slot.slot?.anyPlayer?.displayName
            )?.includes(formatSearchString(playerNameFilter));
          })
          .map(slot => (
            <CollectionSlot
              key={slot.slug}
              userCardCollectionSlot={slot}
              small={small}
              rarity={cardCollection.rarity}
            />
          ))}
      </SlotsGrid>
    </Root>
  );
};

CollectionSlotsPreview.fragments = {
  cardCollection: gql`
    fragment CollectionSlotsPreview_cardCollection on CardCollection {
      slug
      rarity
      userCardCollection(forUserSlug: $userSlug) {
        id
        slots {
          slug
          cardCollectionCardsCount
          slot {
            id
            anyPlayer {
              slug
              displayName
            }
          }
          ...CollectionSlot_userCardCollectionSlot
        }
      }
      ...CollectionPreviewHeader_cardCollection
    }
    ${CollectionPreviewHeader.fragments.cardCollection}
    ${CollectionSlot.fragments.userCardCollectionSlot}
  ` as TypedDocumentNode<CollectionSlotsPreview_cardCollection>,
};
